<template>
  <header class="c-topbar">
    <div
      v-if="current"
      class="c-topbar__title"
    >
      <span class="c-topbar__title-icon">
        <app-icon
          :name="current.icon"
          :size="18"
        />
      </span>
      <span
        v-if="current.parent"
        class="c-topbar__crumb"
      >
        {{ current.parent }}
        <app-icon
          class="c-topbar__crumb-sep"
          name="chevron_right"
          :size="18"
        />
      </span>
      <h1 class="c-topbar__heading">
        {{ current.text }}
      </h1>
    </div>

    <div class="c-topbar__actions">
      <button
        type="button"
        class="ui-btn ui-btn--ghost c-topbar__icon-btn"
        :aria-label="nightMode ? 'Switch to light theme' : 'Switch to dark theme'"
        :title="nightMode ? 'Light theme' : 'Dark theme'"
        @click="$emit('toggle-theme')"
      >
        <app-icon :name="nightMode ? 'light_mode' : 'dark_mode'" />
      </button>

      <span class="c-topbar__divider" />

      <div
        v-click-outside="closeMenu"
        class="c-topbar__user"
      >
        <button
          type="button"
          class="c-topbar__user-btn"
          aria-haspopup="menu"
          :aria-expanded="String(menuOpen)"
          @click="menuOpen = !menuOpen"
        >
          <span class="c-topbar__avatar">{{ initials }}</span>
          <span class="c-topbar__user-meta">
            <span class="c-topbar__user-name">{{ displayName }}</span>
            <span class="c-topbar__user-role">{{ roleLabel }}</span>
          </span>
          <app-icon
            class="c-topbar__user-chevron"
            name="expand_more"
            :size="18"
          />
        </button>

        <transition name="c-topbar-menu">
          <div
            v-if="menuOpen"
            class="c-topbar__menu"
            role="menu"
          >
            <div class="c-topbar__menu-header">
              <span class="c-topbar__avatar c-topbar__avatar--lg">{{ initials }}</span>
              <div class="c-topbar__menu-identity">
                <span class="c-topbar__user-name">{{ displayName }}</span>
                <span class="c-topbar__menu-email">{{ email }}</span>
              </div>
            </div>

            <button
              type="button"
              class="c-topbar__menu-item"
              role="menuitem"
              @click="select('change-password')"
            >
              <app-icon name="key" />
              Change password
            </button>
            <button
              type="button"
              class="c-topbar__menu-item"
              role="menuitem"
              @click="$emit('toggle-theme')"
            >
              <app-icon :name="nightMode ? 'light_mode' : 'dark_mode'" />
              {{ nightMode ? 'Light theme' : 'Dark theme' }}
            </button>

            <div class="c-topbar__menu-sep" />

            <button
              type="button"
              class="c-topbar__menu-item c-topbar__menu-item--danger"
              role="menuitem"
              @click="select('logout')"
            >
              <app-icon name="logout" />
              Log out
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import AppIcon from '../app-icon/AppIcon';

export default {
  name: 'AppTopbar',
  components: {
    AppIcon,
  },
  props: {
    current: {
      type: Object,
      default: null,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    nightMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    menuOpen: false,
  }),
  computed: {
    email() {
      return (this.user && this.user.email) || '';
    },

    displayName() {
      const { firstName, lastName, name } = this.user || {};
      const fullName = [firstName, lastName].filter(Boolean).join(' ') || name;

      if (fullName) {
        return fullName;
      }

      // "jane.doe@x.com" -> "Jane Doe"
      return this.email
        .split('@')[0]
        .split(/[._-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || 'Account';
    },

    initials() {
      return this.displayName
        .split(' ')
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase();
    },

    roleLabel() {
      return this.isAdmin ? 'Administrator' : 'Member';
    },
  },
  methods: {
    closeMenu() {
      this.menuOpen = false;
    },

    select(action) {
      this.menuOpen = false;
      this.$emit(action);
    },
  },
};
</script>

<style lang="scss" scoped>
.c-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  flex-shrink: 0;
  padding: 0 24px;
  background-color: var(--bg-surface);
  border-bottom: 1px solid var(--border-default);
  font-family: var(--font-sans);
  color: var(--text-primary);
}

.c-topbar__icon-btn {
  width: 40px;
  padding: 0;
}

// ---------- title ----------
.c-topbar__title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.c-topbar__title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background-color: var(--brand-50);
  color: var(--brand-600);
}

.c-topbar__crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: var(--text-muted);
  white-space: nowrap;
}

.c-topbar__crumb-sep {
  color: var(--border-strong);
}

.c-topbar__heading {
  margin: 0;
  overflow: hidden;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ---------- actions ----------
.c-topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.c-topbar__divider {
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background-color: var(--border-default);
}

.c-topbar__user {
  position: relative;
}

.c-topbar__user-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 4px 4px;
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  cursor: pointer;
  transition: background-color var(--duration-fast) ease;

  &:hover {
    background-color: var(--bg-hover);
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-topbar__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--brand-500), var(--brand-700));
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.c-topbar__avatar--lg {
  width: 40px;
  height: 40px;
  font-size: 14px;
}

.c-topbar__user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
}

.c-topbar__user-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-topbar__user-role {
  font-size: 12px;
  color: var(--text-muted);
}

.c-topbar__user-chevron {
  color: var(--text-muted);
}

// ---------- dropdown ----------
.c-topbar__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 260px;
  padding: 6px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  box-shadow: var(--shadow-lg);
  z-index: $zIndexDropdown;
}

.c-topbar__menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px 12px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--border-default);
}

.c-topbar__menu-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.c-topbar__menu-email {
  overflow: hidden;
  font-size: 13px;
  color: var(--text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-topbar__menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  .c-app-icon {
    color: var(--text-muted);
  }

  &:hover,
  &:focus-visible {
    background-color: var(--bg-hover);
  }
}

.c-topbar__menu-item--danger {
  color: var(--danger-fg);

  .c-app-icon {
    color: currentColor;
  }

  &:hover,
  &:focus-visible {
    background-color: var(--danger-bg);
  }
}

.c-topbar__menu-sep {
  height: 1px;
  margin: 6px 0;
  background-color: var(--border-default);
}

.c-topbar-menu-enter-active,
.c-topbar-menu-leave-active {
  transition: opacity var(--duration-fast) ease, transform var(--duration-fast) var(--ease-out);
  transform-origin: top right;
}

.c-topbar-menu-enter,
.c-topbar-menu-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(-4px);
}

@media (max-width: 960px) {
  .c-topbar {
    padding: 0 16px;
  }

  .c-topbar__crumb,
  .c-topbar__user-meta,
  .c-topbar__user-chevron {
    display: none;
  }
}
</style>
